import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import { CATEGORY_ICONS } from '@/utils/format';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';

const EXPENSE_CATEGORIES = ['food', 'transport', 'housing', 'entertainment', 'health', 'education', 'shopping', 'services', 'other'];
const INCOME_CATEGORIES = ['salary', 'freelance', 'other'];
const ICON_OPTIONS = ['🏠', '💰', '🎬', '🚗', '💊', '📚', '🛍️', '⚡', '💼', '📌', '🏋️', '🎵'];
const COLOR_OPTIONS = ['#3b82f6', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#f97316'];

export default function AddAgendaItemModal({ open, onClose, item = null }) {
  const { t } = useTranslation();
  const addAgendaItem = useStore((s) => s.addAgendaItem);
  const updateAgendaItem = useStore((s) => s.updateAgendaItem);

  const isEdit = !!item;

  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [type, setType] = useState('expense');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [recurrence, setRecurrence] = useState('none');
  const [icon, setIcon] = useState(ICON_OPTIONS[0]);
  const [color, setColor] = useState(COLOR_OPTIONS[0]);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (item && open) {
      setTitle(item.title || '');
      setAmount(item.amount ? String(item.amount) : '');
      setType(item.type || 'expense');
      setCategory(item.category || '');
      setDate(item.date || new Date().toISOString().split('T')[0]);
      setRecurrence(item.recurrence || 'none');
      setIcon(item.icon || ICON_OPTIONS[0]);
      setColor(item.color || COLOR_OPTIONS[0]);
      setNotes(item.notes || '');
      setErrors({});
    }
  }, [item, open]);

  const categories = type === 'expense' ? EXPENSE_CATEGORIES : INCOME_CATEGORIES;

  const validate = () => {
    const errs = {};
    if (!title.trim()) errs.title = t('agenda.form.titleRequired');
    if (!category) errs.category = t('agenda.form.categoryRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const data = {
      title: title.trim(),
      amount: Number(amount) || 0,
      type,
      category,
      date,
      recurrence,
      icon,
      color,
      notes: notes.trim(),
    };
    if (isEdit) {
      updateAgendaItem(item.id, data);
    } else {
      addAgendaItem(data);
    }
    handleClose();
  };

  const handleClose = () => {
    setTitle('');
    setAmount('');
    setType('expense');
    setCategory('');
    setDate(new Date().toISOString().split('T')[0]);
    setRecurrence('none');
    setIcon(ICON_OPTIONS[0]);
    setColor(COLOR_OPTIONS[0]);
    setNotes('');
    setErrors({});
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title={isEdit ? t('agenda.editItem') : t('agenda.addNew')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4">
        {/* Type toggle */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('agenda.form.type')}
          </label>
          <div className="flex gap-1.5 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
            {['expense', 'income'].map((tp) => (
              <button
                key={tp}
                onClick={() => { setType(tp); setCategory(''); }}
                className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                  type === tp
                    ? tp === 'expense'
                      ? 'bg-danger-500 text-white'
                      : 'bg-success-500 text-white'
                    : 'text-gray-500'
                }`}
              >
                {t(`common.${tp}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('agenda.form.title')}
          </label>
          <input
            type="text"
            className={`input ${errors.title ? 'border-danger-500' : ''}`}
            placeholder={t('agenda.form.titlePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          {errors.title && <p className="text-xs text-danger-500 mt-1">{errors.title}</p>}
        </div>

        {/* Amount */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('agenda.form.amount')}
          </label>
          <input
            type="number"
            inputMode="decimal"
            className="input"
            placeholder="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        {/* Date */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('agenda.form.date')}
          </label>
          <input
            type="date"
            className="input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        {/* Recurrence */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('agenda.form.recurrence')}
          </label>
          <div className="flex gap-1.5 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
            {['none', 'weekly', 'monthly'].map((r) => (
              <button
                key={r}
                onClick={() => setRecurrence(r)}
                className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors ${
                  recurrence === r ? 'bg-brand-600 text-white' : 'text-gray-500'
                }`}
              >
                {t(`agenda.form.recurrence${r.charAt(0).toUpperCase() + r.slice(1)}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Category grid */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('agenda.form.category')}
          </label>
          <div className="grid grid-cols-4 gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 text-xs transition-all ${
                  category === cat
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                    : 'border-gray-200 dark:border-gray-700 hover:border-brand-400'
                }`}
              >
                <span className="text-lg">{CATEGORY_ICONS[cat]}</span>
                <span className="truncate w-full text-center">{t(`categories.${cat}`)}</span>
              </button>
            ))}
          </div>
          {errors.category && <p className="text-xs text-danger-500 mt-1">{errors.category}</p>}
        </div>

        {/* Icon picker */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('agenda.form.icon')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {ICON_OPTIONS.map((ic) => (
              <button
                key={ic}
                onClick={() => setIcon(ic)}
                className={`w-10 h-10 rounded-xl text-lg flex items-center justify-center border-2 transition-all ${
                  icon === ic
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                    : 'border-gray-200 dark:border-gray-700 hover:border-brand-400'
                }`}
              >
                {ic}
              </button>
            ))}
          </div>
        </div>

        {/* Color picker */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('agenda.form.color')}
          </label>
          <div className="flex gap-2">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  color === c ? 'border-gray-900 dark:border-white scale-110' : 'border-transparent'
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('agenda.form.notes')}
          </label>
          <textarea
            className="input min-h-[60px] resize-none"
            placeholder={t('agenda.form.notesPlaceholder')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

      </div>
    </Modal>
  );
}
